import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatPhone } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors";

export default function PublicScreener() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ consumer_name: "", consumer_email: "", consumer_phone: "", answers: {}, consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/screener/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [slug]);

  const setAnswer = (qid, val) => setForm((f) => ({ ...f, answers: { ...f.answers, [qid]: val } }));
  const toggleMulti = (qid, opt) => setForm((f) => {
    const cur = Array.isArray(f.answers[qid]) ? f.answers[qid] : [];
    const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    return { ...f, answers: { ...f.answers, [qid]: next } };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent statement to submit.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/screener-submit", { ...form, deployment_slug: slug });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-32 pb-20 text-center" data-testid="public-screener-error">
        <h1 className="font-display text-2xl font-bold text-slate-900">Screener unavailable</h1>
        <p className="mt-2 text-slate-600">{error}</p>
        <Link to="/" className="mt-6 inline-flex text-sm font-semibold text-blue-700 hover:text-blue-900">
          ← Back to home
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-32 pb-20 text-center text-slate-500" data-testid="public-screener-loading">
        Loading screener…
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-32 pb-20 text-center" data-testid="public-screener-done">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100 mb-5">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Thank you.</h1>
        <p className="mt-3 text-slate-600 max-w-md mx-auto">
          Your responses were sent to <span className="font-semibold text-slate-900">{data.clinic_name}</span>. The team will follow up about next steps.
        </p>
      </div>
    );
  }

  const screener = data.screener;
  const clinic = data.clinic_name;
  const label = data.deployment?.custom_label || screener.name;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-28 pb-20" data-testid="public-screener-page">
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-600 mb-3">{clinic}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
        {label}
      </h1>
      {screener.intro && (
        <p className="mt-4 text-slate-600 leading-relaxed">{screener.intro}</p>
      )}

      <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
        <ShieldCheck className="w-3 h-3" /> Private & vendor-vetted
      </div>

      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="public-screener-form">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Your name *</label>
            <input
              required
              data-testid="public-screener-name"
              value={form.consumer_name}
              onChange={(e) => setForm({ ...form, consumer_name: e.target.value })}
              className={inputCls}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email *</label>
              <input
                type="email"
                required
                data-testid="public-screener-email"
                value={form.consumer_email}
                onChange={(e) => setForm({ ...form, consumer_email: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Phone (optional)</label>
              <input
                data-testid="public-screener-phone"
                value={form.consumer_phone}
                onChange={(e) => setForm({ ...form, consumer_phone: formatPhone(e.target.value) })}
                inputMode="tel"
                placeholder="(555) 123-4567"
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {(screener.questions || []).map((q, idx) => (
          <div key={q.id} data-testid={`public-screener-q-${q.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-1">
              Question {idx + 1}
            </div>
            <div className="font-display text-base font-semibold text-slate-900 mb-3">{q.prompt}</div>
            {q.type === "text" && (
              <textarea
                rows={3}
                data-testid={`public-screener-q-${q.id}-text`}
                value={form.answers[q.id] || ""}
                onChange={(e) => setAnswer(q.id, e.target.value)}
                className={`${inputCls} resize-y`}
              />
            )}
            {q.type === "single" && (
              <div className="space-y-2">
                {(q.options || []).map((opt) => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer rounded-lg px-3 py-2 border border-slate-200 hover:border-slate-400">
                    <input
                      type="radio"
                      name={`q-${q.id}`}
                      data-testid={`public-screener-q-${q.id}-${opt}`}
                      checked={form.answers[q.id] === opt}
                      onChange={() => setAnswer(q.id, opt)}
                    />
                    <span className="text-sm text-slate-800">{opt}</span>
                  </label>
                ))}
              </div>
            )}
            {q.type === "multi" && (
              <div className="space-y-2">
                {(q.options || []).map((opt) => {
                  const checked = (form.answers[q.id] || []).includes(opt);
                  return (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer rounded-lg px-3 py-2 border border-slate-200 hover:border-slate-400">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMulti(q.id, opt)}
                      />
                      <span className="text-sm text-slate-800">{opt}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid="public-screener-consent"
            checked={form.consent}
            onChange={(e) => setForm({ ...form, consent: e.target.checked })}
            className="mt-1"
          />
          <span>
            I consent to share these responses with <strong>{clinic}</strong> and to be contacted about relevant programs.
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting}
          data-testid="public-screener-submit"
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
        >
          <Sparkles className="w-3.5 h-3.5" /> {submitting ? "Submitting…" : "Submit"}
        </button>
      </form>
    </div>
  );
}
